import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import routeUrls from "../COMPONENTS/navbar/navLink";
import { SkiGame, HudState, GameOver, SOFT_STRIKES, Look, DEFAULT_LOOK } from "../COMPONENTS/skigame/game";
import { sfx } from "../COMPONENTS/skigame/sound";
import {
  Score, topScores, myBest, myName, submitScore, submitRun, renameRun, renamePlayer, fmtScore,
  guestId, guestName, guestBest, submitGuest, renameGuest, cleanName, GUEST_NAME_MAX,
} from "../COMPONENTS/skigame/scores";
import { SHOP, Pick as GearPick, Wallet, balance, canWear, wearable, priceOf, loadWallet, earnBuns, buyItem } from "../COMPONENTS/skigame/shop";
import "../CSS/skigame.scss";

interface Props {
  player: { uid: string; name: string } | null;
  authLoading: boolean;
}

type Ui = "ready" | "count" | "play" | "over";

const EMPTY_HUD: HudState = { score: 0, dist: 0, buns: 0, strikes: 0, shield: 0, slow: 0, stage: "Slussen", count: 0, cross: false };

const savedCtrl = (): "swipe" | "tap" => {
  try {
    return localStorage.getItem("skigame-controls") === "tap" ? "tap" : "swipe";
  } catch {
    return "swipe";
  }
};

// The skier's look, remembered on this device
const savedLook = (): Look => {
  try {
    return { ...DEFAULT_LOOK, ...JSON.parse(localStorage.getItem("skigame-look") ?? "{}") };
  } catch {
    return DEFAULT_LOOK;
  }
};

// The shop shows one kind of gear at a time
const GEAR: GearPick[] = SHOP.flatMap(([, picks]) => picks);

export const SkiGameView = ({ player, authLoading }: Props) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const gameRef = useRef<SkiGame | null>(null);
  const ctrlRef = useRef<"swipe" | "tap">(savedCtrl());
  const [ui, setUi] = useState<Ui>("ready");
  const [hud, setHud] = useState<HudState>(EMPTY_HUD);
  const [result, setResult] = useState<GameOver | null>(null);
  const [board, setBoard] = useState<Score[] | null>(null);
  const [best, setBest] = useState<number | null>(null);
  const [muted, setMuted] = useState(sfx.muted);
  const [musicMuted, setMusicMuted] = useState(sfx.musicMuted);
  const [saved, setSaved] = useState<"idle" | "saving" | "new" | "same" | "err">("idle");
  const [gname, setGname] = useState(guestName);
  const [draft, setDraft] = useState(gname);
  const [ctrl, setCtrl] = useState<"swipe" | "tap">(() => ctrlRef.current);
  const [info, setInfo] = useState(false);
  const [look, setLook] = useState(savedLook);
  const [dressing, setDressing] = useState(false); // the look editor is open
  const [wallet, setWallet] = useState<Wallet | null>(null);
  // An item being tried on before it is bought
  const [trial, setTrial] = useState<{ key: keyof Look; value: string } | null>(null);
  const [buying, setBuying] = useState(false);
  const [gearTab, setGearTab] = useState(0);
  const [naming, setNaming] = useState(false); // guest is typing a (new) name
  const uid = player?.uid ?? null;
  const [pname, setPname] = useState<string | null>(null); // signed-in player's name on the list
  const name = pname ?? cleanName(player?.name ?? "");
  // The name shown and saved: the player's, or this device's guest name
  const shown = player ? name : gname;
  // Whose row to highlight: the signed-in player, or this device as a guest
  const me = uid ?? (authLoading ? null : "g:" + guestId());

  // This run's row on the list; each run is saved once
  const [runId, setRunId] = useState<string | null>(null);
  const runSaved = useRef(false);
  const saveRun = useCallback((who: string, n: string, score: number) => {
    if (runSaved.current) return Promise.resolve();
    runSaved.current = true;
    // The best is already saved, so a missing row is no reason to show an error
    return submitRun(who, n, score).then(setRunId).catch(() => {});
  }, []);

  const loadBoard = useCallback(() => {
    topScores(10).then(setBoard).catch(() => setBoard([]));
  }, []);

  useEffect(loadBoard, [loadBoard]);
  useEffect(() => {
    if (authLoading) return;
    (uid ? myBest(uid) : guestBest()).then(setBest).catch(() => setBest(null));
    setPname(null);
    if (uid) myName(uid).then((n) => n && setPname(n)).catch(() => {});
    setWallet(null);
    if (uid) loadWallet(uid).then(setWallet).catch(() => {});
  }, [uid, authLoading]);

  // What the skier wears: owned gear, plus whatever is being tried on
  const worn = wearable(look, wallet);
  const shownLook = trial ? { ...worn, [trial.key]: trial.value } : worn;
  const lookKey = JSON.stringify(shownLook);
  useEffect(() => {
    gameRef.current?.setCloseUp(dressing);
  }, [dressing]);
  useEffect(() => {
    gameRef.current?.setLook(JSON.parse(lookKey));
  }, [lookKey]);

  const saveGuest = useCallback((n: string, score: number) => {
    setSaved("saving");
    submitGuest(n, score)
      .then((isNew) => saveRun("g:" + guestId(), n, score).then(() => isNew))
      .then((isNew) => {
        setSaved(isNew ? "new" : "same");
        setBest((b) => (b === null || score > b ? score : b));
        loadBoard();
      })
      .catch(() => setSaved("err"));
  }, [loadBoard, saveRun]);

  // Game-over handler reads the latest player through a ref (the game is created once)
  const overRef = useRef<(r: GameOver) => void>(() => {});
  overRef.current = (r: GameOver) => {
    setResult(r);
    setUi("over");
    sfx.stopMusic();
    if (!uid) {
      // Guests who already picked a name are saved straight away
      if (gname) saveGuest(gname, r.score);
      else setNaming(true);
      return;
    }
    if (r.score >= 1) earnBuns(uid, r.score).then(setWallet).catch(() => {});
    setSaved("saving");
    submitScore(uid, name, r.score)
      .then((isNew) => saveRun(uid, name, r.score).then(() => isNew))
      .then((isNew) => {
        setSaved(isNew ? "new" : "same");
        if (isNew) setBest(r.score);
        loadBoard();
      })
      .catch(() => setSaved("err"));
  };

  useEffect(() => {
    const game = new SkiGame(hostRef.current!, {
      onHud: (s) => {
        setHud(s);
        setUi((u) => (u === "count" && s.count === 0 ? "play" : u));
      },
      onOver: (r) => overRef.current(r),
    });
    gameRef.current = game;
    game.controls = ctrlRef.current;
    game.setLook(wearable(savedLook(), null));
    return () => {
      game.dispose();
      sfx.dispose();
    };
  }, []);

  const start = useCallback(() => {
    setResult(null);
    setTrial(null); // nothing unbought goes along on the run
    setDressing(false);
    setRunId(null);
    runSaved.current = false;
    setSaved("idle");
    setNaming(false);
    setUi("count");
    sfx.unlock();
    sfx.startMusic();
    gameRef.current?.start();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;
      if ((ui === "ready" || ui === "over") && (e.key === " " || e.key === "Enter")) {
        e.preventDefault();
        start();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ui, start]);

  const rank = runId && board ? board.findIndex((s) => s.id === runId) + 1 : 0;

  const submitName = (e: React.FormEvent) => {
    e.preventDefault();
    const n = cleanName(draft);
    if (!n) return;
    setDraft(n);
    setNaming(false);
    // The new name is for the run just played (if any) and the next ones
    if (uid) {
      setPname(n);
      renamePlayer(uid, n);
    } else {
      setGname(n);
      renameGuest(n);
      // A guest's first run waits for a name before it is saved
      if (result && !runSaved.current) return saveGuest(n, result.score);
    }
    if (result && runId) renameRun(runId, n).then(loadBoard).catch(() => {});
  };

  const NameForm = () => (
    <form className="skigame__name" onSubmit={submitName}>
      <label htmlFor="skigame-name">Ditt namn på topplistan</label>
      <span>
        <input
          id="skigame-name"
          value={draft}
          maxLength={GUEST_NAME_MAX}
          autoComplete="nickname"
          autoCapitalize="words"
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
        />
        <button type="submit" disabled={!cleanName(draft)}>Spara</button>
      </span>
    </form>
  );

  const Board = ({ rows }: { rows: Score[] }) => (
    <ol className="skigame__board">
      {rows.map((s, i) => (
        <li key={s.id} className={s.uid === me ? "skigame__me" : undefined}>
          <span className="skigame__pos">{i + 1}</span>
          <span className="skigame__who">{s.name}</span>
          <span className="skigame__ms">{fmtScore(s.score)}</span>
        </li>
      ))}
    </ol>
  );

  const pickLook = (key: keyof Look, value: string, w = wallet) => {
    if (!canWear(w, key, value)) {
      setTrial({ key, value });
      return;
    }
    setTrial(null);
    const next = { ...look, [key]: value } as Look;
    setLook(next);
    gameRef.current?.setLook(next);
    try {
      localStorage.setItem("skigame-look", JSON.stringify(next));
    } catch {
      // storage blocked: the look lasts for this visit only
    }
  };

  const buy = () => {
    if (!uid || !trial) return;
    setBuying(true);
    buyItem(uid, trial.key, trial.value)
      .then((w) => {
        setWallet(w);
        pickLook(trial.key, trial.value, w);
      })
      .catch(() => loadWallet(uid).then(setWallet).catch(() => {}))
      .finally(() => setBuying(false));
  };

  const closeLook = () => {
    setTrial(null);
    setDressing(false);
  };

  const trialPrice = trial ? priceOf(trial.key, trial.value) : 0;

  const pickCtrl = (c: "swipe" | "tap") => {
    setCtrl(c);
    ctrlRef.current = c;
    if (gameRef.current) gameRef.current.controls = c;
    try {
      localStorage.setItem("skigame-controls", c);
    } catch {
      // not remembered, that's fine
    }
  };
  const toggleSound = () => {
    sfx.setMuted(!muted);
    setMuted(!muted);
    sfx.unlock();
  };
  const toggleMusic = () => {
    sfx.unlock();
    sfx.setMusicMuted(!musicMuted);
    setMusicMuted(!musicMuted);
  };


  return (
    <div className="skigame">
      <div className="skigame__host" ref={hostRef} />

      <div className="skigame__top">
        <span className="skigame__left">
          <NavLink to={routeUrls.PROJEKT} className="skigame__back">← Projekt</NavLink>
          <button type="button" className="skigame__back skigame__sound" onClick={toggleSound} aria-label={muted ? "Slå på ljudeffekter" : "Stäng av ljudeffekter"} aria-pressed={!muted}>
            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" shapeRendering="crispEdges">
              <path fill="currentColor" d="M1 5h3l4-3v12l-4-3H1z" />
              {muted ? (
                <path fill="currentColor" d="M10 5h2v2h-2zM14 5h2v2h-2zM12 7h2v2h-2zM10 9h2v2h-2zM14 9h2v2h-2z" />
              ) : (
                <path fill="currentColor" d="M10 6h2v4h-2zM13 4h2v8h-2z" />
              )}
            </svg>
          </button>
          <button type="button" className="skigame__back skigame__sound" onClick={toggleMusic} aria-label={musicMuted ? "Slå på musik" : "Stäng av musik"} aria-pressed={!musicMuted}>
            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" shapeRendering="crispEdges">
              <path fill="currentColor" d="M5 2h9v3H8v7a2 2 0 1 1-3-1.7zM11 5h3v6a2 2 0 1 1-3-1.7z" />
              {musicMuted && <path fill="none" stroke="currentColor" strokeWidth="2" d="M1 1l14 14" />}
            </svg>
          </button>
        </span>
        {ui !== "ready" && (
          <div className="skigame__hud">
            <span className="skigame__time">
              {fmtScore(hud.score)} <span className="skigame__unit">{hud.score === 1 ? "bulle" : "bullar"}</span>
            </span>
            <span className="skigame__stats">
              <span>{hud.stage}</span>
              <span>{fmtScore(hud.dist)} m</span>
            </span>
            <span className="skigame__stats">
              {hud.strikes > 0 && <span className="skigame__bad">Krock {hud.strikes}/{SOFT_STRIKES}</span>}
              {hud.shield > 0 && <span className="skigame__pow">Odödlig {Math.ceil(hud.shield)} s</span>}
              {hud.slow > 0 && <span className="skigame__pow">Rött ljus {Math.ceil(hud.slow)} s</span>}
            </span>
          </div>
        )}
      </div>

      {ui === "count" && hud.count > 0 && <div className="skigame__count">{hud.count}</div>}

      {ui === "ready" && dressing && (
        <div className="skigame__panel skigame__panel--look">
          {player ? (
            <p className="skigame__wallet">Du har <b>{fmtScore(wallet ? balance(wallet) : 0)}</b> bullar att handla för</p>
          ) : (
            <p className="skigame__wallet">Gratis grejer kan alla ta. <NavLink to={`${routeUrls.LOGIN}?from=${routeUrls.SKIGAME}`}>Logga in</NavLink> för att köpa resten för bullarna du åker ihop.</p>
          )}
          <div className="skigame__tabs" role="tablist" aria-label="Utrustning">
            {GEAR.map((p, i) => (
              <button key={p.key} type="button" role="tab" aria-selected={gearTab === i} onClick={() => setGearTab(i)}>
                {p.label}
              </button>
            ))}
          </div>
          {[GEAR[gearTab]].map((p) => (
            <div key={p.key} className="skigame__pick" role="tabpanel" aria-label={p.label}>
              {p.items.map((it) => {
                const on = shownLook[p.key] === it.value;
                const locked = !canWear(wallet, p.key, it.value);
                return (
                  <button
                    key={it.value}
                    type="button"
                    className={(it.label ? "" : "skigame__swatch") + (locked ? " skigame__locked" : "")}
                    style={it.label ? undefined : { background: it.value }}
                    aria-label={`${it.label ?? p.label}${locked ? `, ${it.price} bullar` : ""}`}
                    aria-pressed={on}
                    onClick={() => pickLook(p.key, it.value)}
                  >
                    {it.label}
                    {locked && <small>{it.price}</small>}
                  </button>
                );
              })}
            </div>
          ))}
          {trial && (
            <div className="skigame__buy">
              {!player ? (
                <span>Kostar {trialPrice} bullar. Logga in för att köpa.</span>
              ) : wallet && balance(wallet) >= trialPrice ? (
                <>
                  <span>Kostar {trialPrice} bullar</span>
                  <button type="button" onClick={buy} disabled={buying}>{buying ? "Köper…" : "Köp"}</button>
                </>
              ) : (
                <span>Kostar {trialPrice} bullar. Du behöver {trialPrice - (wallet ? balance(wallet) : 0)} till.</span>
              )}
            </div>
          )}
          <button type="button" className="skigame__go" onClick={closeLook}>Klar</button>
        </div>
      )}

      {ui === "ready" && !dressing && (
        <div className="skigame__panel">
          <h1 className="skigame__title">Rullskidor i stan<span>.</span></h1>
          {info && (
            <>
              <p className="skigame__route">Samla kanelbullar, 1 poäng styck. Trafiken kommer emot dig, allt snabbare.</p>
          <ul className="skigame__rules">
            <li><b>← →</b> / <b>A D</b>, svep eller tryck på vänster/höger halva byter fil. Ingen broms.</li>
            <li>Rakt framifrån: bil, taxi, buss är <b>slut</b>, cykel och sparkcykel är en av <b>två krockar</b>. Från sidan: cyklisten <b>åker av</b> utan straff, en bil kostar en av två krockar.</li>
            <li>Trafiken byter fil (blinkar först). Se upp för <b>röda sportbilar</b> och <b>spårvagnen</b>.</li>
            <li>Vid <b>övergångsställen</b>: hitta luckan mellan fotgängarna.</li>
            <li>Vid <b>vägarbeten</b>: kör upp på rampen och flyg över gropen. Bullar hänger i luften.</li>
          </ul>
          <ul className="skigame__powers">
            <li><i className="skigame__ico skigame__ico--bun" />Kanelbulle <b>+1, och du åker fortare</b></li>
            <li><i className="skigame__ico skigame__ico--soup" />Blåbärssoppa <b>+2</b></li>
            <li><i className="skigame__ico skigame__ico--shield" />Gel <b>odödlig</b></li>
            <li><i className="skigame__ico skigame__ico--slow" />Rött ljus <b>sakta in</b></li>
          </ul>
            </>
          )}
          <button type="button" className="skigame__go" onClick={start}>Starta</button>
          <div className="skigame__ctrl" role="group" aria-label="Styrning på mobil">
            <span>Styr med</span>
            <button type="button" aria-pressed={ctrl === "swipe"} onClick={() => pickCtrl("swipe")}>Svep</button>
            <button type="button" aria-pressed={ctrl === "tap"} onClick={() => pickCtrl("tap")}>Tryck</button>
          </div>
          <button type="button" className="skigame__link skigame__infobtn" onClick={() => setDressing(true)}>Din åkare</button>
          <button type="button" className="skigame__link skigame__infobtn" aria-expanded={info} onClick={() => setInfo((v) => !v)}>
            {info ? "Dölj info" : "Så spelar du"}
          </button>
          {best !== null && <p className="skigame__note">Ditt rekord: <b>{fmtScore(best)}</b> {best === 1 ? "bulle" : "bullar"}</p>}
          {!authLoading && (naming ? NameForm() : (
            <p className="skigame__note">
              {shown ? (
                <>
                  Du sparas på topplistan som <b>{shown}</b> ·{" "}
                  <button type="button" className="skigame__link" onClick={() => { setDraft(shown); setNaming(true); }}>byt namn</button>
                </>
              ) : "Efter åket skriver du ditt namn till topplistan."}
            </p>
          ))}
          {board && board.length > 0 && (
            <>
              <h2 className="skigame__label">Topplista</h2>
              <Board rows={board.slice(0, 5)} />
            </>
          )}
        </div>
      )}

      {ui === "over" && result && (
        <div className="skigame__panel">
          <p className="skigame__label">Krock vid {hud.stage}</p>
          <p className="skigame__big">{fmtScore(result.score)}</p>
          <p className="skigame__note">
            {result.buns === 1 ? "bulle" : "bullar"} · {fmtScore(result.dist)} m
          </p>
          {naming ? (
            NameForm()
          ) : (
            <p className="skigame__saved">
              {saved === "saving" && "Sparar…"}
              {saved === "new" && "Nytt rekord!"}
              {saved === "same" && best !== null && `Ditt rekord är ${fmtScore(best)}.`}
              {(saved === "new" || saved === "same") && rank > 0 && ` Du är ${rank}:a på topplistan.`}
              {saved === "err" && "Kunde inte spara poängen."}
              {shown && saved !== "saving" && (
                <>
                  {" "}Sparas som {shown} ·{" "}
                  <button type="button" className="skigame__link" onClick={() => { setDraft(shown); setNaming(true); }}>byt namn</button>
                </>
              )}
            </p>
          )}
          <button type="button" className="skigame__go" onClick={start}>Åk igen</button>
          {board && board.length > 0 && (
            <>
              <h2 className="skigame__label">Topplista</h2>
              <Board rows={board} />
            </>
          )}
        </div>
      )}
    </div>
  );
};
